import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaDrawPolygon,
  FaExpand,
  FaExternalLinkAlt,
  FaMapMarkedAlt,
  FaPlus,
  FaTh,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  fetchGroupFarms,
  fetchGroupLandPlots,
  landPlotAddUrl,
  landPlotsForFarms,
  sumLandPlotAreaHa,
} from "./serviceOrderLocationApi";
import {
  mergeApplicationAreaSummary,
  summarizeCleanFenceSelection,
} from "../../utils/mapMeasurements";
import ServiceOrderLocationMapModal from "./ServiceOrderLocationMapModal";
import ServiceOrderManualApplicationMapModal from "./ServiceOrderManualApplicationMapModal";
import ServiceOrderKmlImportButton from "./ServiceOrderKmlImportButton";
import "./ServiceOrderLocationFields.css";
import "./ServiceOrderLocationMapModal.css";

const CLEAN_FENCE_NAME = "Cerca limpa";

function ServiceOrderLocationFields({
  farmId,
  selectedServiceFarmIds,
  selectedLandPlotIds,
  onServiceFarmIdsChange,
  onLandPlotIdsChange,
  onApplicationAreaChange,
  initialApplicationArea,
  t,
}) {
  const [groupFarms, setGroupFarms] = useState([]);
  const [allLandPlots, setAllLandPlots] = useState([]);
  const [loadingFarms, setLoadingFarms] = useState(false);
  const [loadingPlots, setLoadingPlots] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [manualMapModalOpen, setManualMapModalOpen] = useState(false);
  const [manualPieces, setManualPieces] = useState([]);
  const [locationAreaSummary, setLocationAreaSummary] = useState(null);
  const [cleanFenceWidthM, setCleanFenceWidthM] = useState(0);
  const lastApplicationAreaKeyRef = useRef("");

  const selectedFarmIds = Array.isArray(selectedServiceFarmIds)
    ? selectedServiceFarmIds
    : [];
  const selectedPlotIds = Array.isArray(selectedLandPlotIds)
    ? selectedLandPlotIds
    : [];

  const loadFarms = useCallback(async () => {
    if (!farmId) return;
    setLoadingFarms(true);
    try {
      setGroupFarms(await fetchGroupFarms(farmId));
    } catch (error) {
      setGroupFarms([]);
      toast.error(t("Não foi possível carregar as fazendas."), {
        position: "bottom-right",
      });
    } finally {
      setLoadingFarms(false);
    }
  }, [farmId, t]);

  const loadLandPlots = useCallback(async () => {
    if (!farmId) return;
    setLoadingPlots(true);
    try {
      setAllLandPlots(await fetchGroupLandPlots(farmId));
    } catch (error) {
      setAllLandPlots([]);
    } finally {
      setLoadingPlots(false);
    }
  }, [farmId]);

  const refreshLocationData = useCallback(async () => {
    await Promise.all([loadFarms(), loadLandPlots()]);
  }, [loadFarms, loadLandPlots]);

  useEffect(() => {
    refreshLocationData();
  }, [refreshLocationData]);

  useEffect(() => {
    if (!initialApplicationArea) {
      setManualPieces([]);
      return;
    }
    setManualPieces(
      Array.isArray(initialApplicationArea.manualPieces)
        ? initialApplicationArea.manualPieces
        : []
    );
  }, [initialApplicationArea]);

  const visibleLandPlots = useMemo(
    () => landPlotsForFarms(allLandPlots, selectedFarmIds),
    [allLandPlots, selectedFarmIds]
  );

  const visiblePlotIds = useMemo(
    () => new Set(visibleLandPlots.map((plot) => plot.id)),
    [visibleLandPlots]
  );

  const selectionSummary = useMemo(() => {
    let plotSummary;

    if (locationAreaSummary && locationAreaSummary.count > 0) {
      plotSummary = {
        ...locationAreaSummary,
        plotCount: locationAreaSummary.plotCount || locationAreaSummary.count || 0,
      };
    } else {
      const workHa = sumLandPlotAreaHa(allLandPlots, selectedPlotIds);
      if (cleanFenceWidthM > 0 && selectedPlotIds.length > 0) {
        const computed = summarizeCleanFenceSelection(
          allLandPlots,
          selectedPlotIds,
          cleanFenceWidthM,
          null
        );
        plotSummary = {
          ...computed,
          plotCount: computed.count || 0,
        };
      } else {
        plotSummary = {
          plotCount: selectedPlotIds.length,
          count: selectedPlotIds.length,
          totalHa: workHa,
          workHa,
          fenceHa: 0,
          cleanFenceWidthM: 0,
        };
      }
    }

    return mergeApplicationAreaSummary(plotSummary, manualPieces);
  }, [
    allLandPlots,
    selectedPlotIds,
    locationAreaSummary,
    cleanFenceWidthM,
    manualPieces,
  ]);

  useEffect(() => {
    if (!onApplicationAreaChange) return;

    const nextSummary =
      selectionSummary.plotCount > 0 || selectionSummary.manualPieceCount > 0
        ? {
            ...selectionSummary,
            cleanFenceWidthM,
          }
        : null;
    const nextKey = nextSummary ? JSON.stringify(nextSummary) : "";
    if (nextKey === lastApplicationAreaKeyRef.current) return;

    lastApplicationAreaKeyRef.current = nextKey;
    onApplicationAreaChange(nextSummary);
  }, [selectionSummary, cleanFenceWidthM, onApplicationAreaChange]);

  const handleManualPiecesConfirm = (pieces) => {
    setManualPieces(pieces);
  };

  useEffect(() => {
    onLandPlotIdsChange((current) => {
      const ids = Array.isArray(current) ? current : [];
      const next = ids.filter((id) => visiblePlotIds.has(id));
      return next.length === ids.length ? current : next;
    });
  }, [visiblePlotIds, onLandPlotIdsChange]);

  const toggleFarm = (farmIdValue) => {
    onServiceFarmIdsChange((current) => {
      const ids = Array.isArray(current) ? current : [];
      return ids.includes(farmIdValue)
        ? ids.filter((id) => id !== farmIdValue)
        : [...ids, farmIdValue];
    });
  };

  const toggleLandPlot = (plotId) => {
    onLandPlotIdsChange((current) => {
      const ids = Array.isArray(current) ? current : [];
      return ids.includes(plotId)
        ? ids.filter((id) => id !== plotId)
        : [...ids, plotId];
    });
  };

  const handleMapConfirm = ({
    serviceFarmIds,
    landPlotIds,
    cleanFenceWidthM: fenceWidth,
    areaSummary,
  }) => {
    if (Array.isArray(serviceFarmIds) && serviceFarmIds.length > 0) {
      onServiceFarmIdsChange(serviceFarmIds);
    }
    if (Array.isArray(landPlotIds)) {
      onLandPlotIdsChange(landPlotIds);
    }
    const width = Number(fenceWidth) || 0;
    setCleanFenceWidthM(width);
    const summary = areaSummary
      ? {
          ...areaSummary,
          plotCount: areaSummary.plotCount || areaSummary.count || 0,
          cleanFenceWidthM: width,
        }
      : null;
    setLocationAreaSummary(summary);
    if (onApplicationAreaChange) {
      onApplicationAreaChange(summary);
    }
  };

  const showFarmNameOnPlots = selectedFarmIds.length > 1;
  const addLandPlotFarmId = selectedFarmIds[0] || null;

  return (
    <div className="service-order-location">
      <div className="service-order-location__map-card">
        <div className="service-order-location__map-actions">
          <button
            type="button"
            className="service-order-location__map-open-btn"
            onClick={() => setMapModalOpen(true)}
          >
            <FaExpand size={18} />
            <span>{t("Escolher Talhões")}</span>
          </button>
          <button
            type="button"
            className="service-order-location__map-open-btn is-manual"
            onClick={() => setManualMapModalOpen(true)}
          >
            <FaDrawPolygon size={18} />
            <span>{t("Desenhar manualmente")}</span>
          </button>
          <ServiceOrderKmlImportButton
            t={t}
            manualPieces={manualPieces}
            onManualPiecesChange={setManualPieces}
          />
        </div>
        <div className="service-order-location__map-card-text">
          <strong>
            <FaMapMarkedAlt style={{ marginRight: 6 }} />
            {t("Local do serviço")}
          </strong>
          <p>
            {t(
              "Abra o mapa da fazenda, clique nos talhões ou cadastre uma fazenda desenhando o perímetro."
            )}
          </p>
          {selectionSummary.plotCount > 0 || selectionSummary.manualPieceCount > 0 ? (
            <div className="service-order-location__area-summary">
              <strong>
                {t("Área total da aplicação")}:{" "}
                {selectionSummary.totalHa.toFixed(2)} ha
              </strong>
              <div className="service-order-location__area-summary-lines">
                {selectionSummary.plotCount > 0 ? (
                  <span>
                    {selectionSummary.plotCount} {t("talhão(ões)")} ·{" "}
                    {selectionSummary.workHa.toFixed(2)} ha
                  </span>
                ) : null}
                {selectionSummary.manualPieceCount > 0 ? (
                  <span>
                    {selectionSummary.manualPieceCount} {t("trecho(s) manual(is)")} ·{" "}
                    {selectionSummary.manualHa.toFixed(2)} ha
                  </span>
                ) : null}
                {cleanFenceWidthM > 0 && selectionSummary.fenceHa > 0 ? (
                  <span>
                    + {CLEAN_FENCE_NAME} {selectionSummary.fenceHa.toFixed(2)} ha @{" "}
                    {cleanFenceWidthM} m
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {selectionSummary.plotCount > 0 || selectionSummary.manualPieceCount > 0 ? (
        <div className="service-order-location__area-summary-card">
          <strong>{t("Resumo da área selecionada")}</strong>
          <div className="service-order-location__area-summary-grid">
            {selectionSummary.plotCount > 0 ? (
              <>
                <span>{t("Talhões")}</span>
                <span>{selectionSummary.workHa.toFixed(2)} ha</span>
              </>
            ) : null}
            {selectionSummary.manualPieceCount > 0 ? (
              <>
                <span>{t("Trechos manuais")}</span>
                <span>{selectionSummary.manualHa.toFixed(2)} ha</span>
              </>
            ) : null}
            {cleanFenceWidthM > 0 && selectionSummary.fenceHa > 0 ? (
              <>
                <span>
                  {CLEAN_FENCE_NAME} ({cleanFenceWidthM} m)
                </span>
                <span>+ {selectionSummary.fenceHa.toFixed(2)} ha</span>
              </>
            ) : null}
            <span className="is-total">{t("Total da aplicação")}</span>
            <span className="is-total">
              {selectionSummary.totalHa.toFixed(2)} ha
            </span>
          </div>
        </div>
      ) : null}

      {manualMapModalOpen ? (
        <ServiceOrderManualApplicationMapModal
          open
          farmId={farmId}
          onClose={() => setManualMapModalOpen(false)}
          onConfirm={handleManualPiecesConfirm}
          initialManualPieces={manualPieces}
          t={t}
        />
      ) : null}

      {mapModalOpen ? (
        <ServiceOrderLocationMapModal
          open
          farmId={farmId}
          onClose={() => setMapModalOpen(false)}
          onConfirm={handleMapConfirm}
          onDataRefresh={refreshLocationData}
          selectedServiceFarmIds={selectedServiceFarmIds}
          selectedLandPlotIds={selectedLandPlotIds}
          initialCleanFenceWidthM={cleanFenceWidthM}
          t={t}
        />
      ) : null}

      <div className="create-schedule-field">
        <label>
          <FaMapMarkedAlt style={{ marginRight: 6 }} />
          {t("Fazendas (local do serviço)")}
          <span className="service-order-location__optional">
            {" "}
            ({t("opcional")})
          </span>
        </label>
        <p className="create-schedule-hint">
          {t(
            "Selecione uma ou mais fazendas. Usa os mesmos cadastros de Cadastros → Fazendas."
          )}
        </p>

        {loadingFarms ? (
          <p>{t("Carregando fazendas...")}</p>
        ) : (
          <>
            <div className="service-order-location__plots">
              {groupFarms.length === 0 ? (
                <p className="service-order-location__placeholder">
                  {t("Nenhuma fazenda cadastrada ainda.")}
                </p>
              ) : (
                groupFarms.map((farm) => (
                  <label
                    key={farm.id}
                    className="service-order-location__plot-item"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFarmIds.includes(farm.id)}
                      onChange={() => toggleFarm(farm.id)}
                    />
                    <span>{farm.nome_da_fazenda}</span>
                  </label>
                ))
              )}
            </div>
          </>
        )}
      </div>

      <div className="create-schedule-field">
        <label>
          <FaTh style={{ marginRight: 6 }} />
          {t("Talhões")}
          <span className="service-order-location__optional">
            {" "}
            ({t("opcional")})
          </span>
        </label>
        <p className="create-schedule-hint">
          {t(
            "Marque os talhões das fazendas selecionadas. Se escolher mais de uma fazenda, aparecem os talhões de todas."
          )}
        </p>

        {selectedFarmIds.length === 0 ? (
          <p className="service-order-location__placeholder">
            {t("Selecione ao menos uma fazenda ou use o mapa acima.")}
          </p>
        ) : loadingPlots ? (
          <p>{t("Carregando talhões...")}</p>
        ) : (
          <>
            <div className="service-order-location__plots">
              {visibleLandPlots.length === 0 ? (
                <p className="service-order-location__placeholder">
                  {t("Nenhum talhão cadastrado nas fazendas selecionadas.")}
                </p>
              ) : (
                visibleLandPlots.map((plot) => (
                  <label
                    key={plot.id}
                    className="service-order-location__plot-item"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlotIds.includes(plot.id)}
                      onChange={() => toggleLandPlot(plot.id)}
                    />
                    <span>
                      {plot.name}
                      {plot.area_h2 ? ` — ${plot.area_h2} ha` : ""}
                      {showFarmNameOnPlots && plot.farm_name
                        ? ` (${plot.farm_name})`
                        : ""}
                    </span>
                  </label>
                ))
              )}
            </div>

            <Link
              to={landPlotAddUrl(farmId, addLandPlotFarmId)}
              className="btn btn-default service-order-location__add-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaPlus style={{ marginRight: 6 }} />
              {t("Cadastrar talhão")}
              <FaExternalLinkAlt style={{ marginLeft: 6 }} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default ServiceOrderLocationFields;
